/**
 * Game Service
 *
 * Handles game loading, configuration, and catalog management.
 */

import { GameRegistry, GameConfig, RegisteredGame } from '../components/game';

// Lazy game loaders - games are loaded on demand
const gameLoaders = import.meta.glob('../../games/*/index.tsx');

/**
 * Load all games into the registry
 * Games register themselves when imported
 */
export async function loadGames(): Promise<void> {
  // Load all game modules - they auto-register with GameRegistry
  const loaderPromises = Object.values(gameLoaders).map((loader) => loader());
  await Promise.all(loaderPromises);
}

/**
 * Load a specific game by ID (lazy load)
 */
export async function loadGame(
  gameId: string
): Promise<RegisteredGame | undefined> {
  const loaderEntry = Object.entries(gameLoaders).find(([path]) =>
    path.includes(`/${gameId}/index.tsx`)
  );

  if (loaderEntry) {
    const [, loader] = loaderEntry;
    await loader(); // This triggers the game's self-registration
  }

  return GameRegistry.get(gameId);
}

/**
 * Get all available games
 */
export function getAllGames(): RegisteredGame[] {
  return GameRegistry.getAll();
}

/**
 * Get a game by ID
 */
export function getGame(gameId: string): RegisteredGame | undefined {
  return GameRegistry.get(gameId);
}

/**
 * Get games by category
 */
export function getGamesByCategory(
  category: GameConfig['category']
): RegisteredGame[] {
  return GameRegistry.getByCategory(category);
}

/**
 * Get games available to user based on their tier
 */
export function getAvailableGames(
  userTier: 'free' | 'premium'
): RegisteredGame[] {
  const allGames = GameRegistry.getAll();

  if (userTier === 'premium') {
    return allGames;
  }

  return allGames.filter((game) => game.config.tier === 'free');
}

/**
 * Check if user can play a specific game
 */
export function canPlayGame(
  gameId: string,
  userTier: 'free' | 'premium'
): boolean {
  const game = GameRegistry.get(gameId);
  if (!game) return false;

  if (userTier === 'premium') return true;
  return game.config.tier === 'free';
}

/**
 * Get featured games for the home page
 */
export function getFeaturedGames(limit = 4): RegisteredGame[] {
  return GameRegistry.getFreeGames().slice(0, limit);
}

/**
 * Search games by title or description
 */
export function searchGames(query: string): RegisteredGame[] {
  const lowerQuery = query.toLowerCase();
  return GameRegistry.getAll().filter(
    (game) =>
      game.config.title.toLowerCase().includes(lowerQuery) ||
      game.config.description.toLowerCase().includes(lowerQuery)
  );
}

/**
 * Get game statistics
 */
export function getGameStats(): {
  total: number;
  free: number;
  premium: number;
  byCategory: Record<string, number>;
} {
  const all = GameRegistry.getAll();
  const byCategory: Record<string, number> = {};

  for (const game of all) {
    const cat = game.config.category;
    byCategory[cat] = (byCategory[cat] || 0) + 1;
  }

  return {
    total: all.length,
    free: GameRegistry.getFreeGames().length,
    premium: GameRegistry.getPremiumGames().length,
    byCategory,
  };
}
